import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, Input, Pagination, useToast } from '@centraoneplus/ui';
import {
  Zap,
  Plus,
  Search,
  Edit,
  Trash2,
  Copy,
  Power,
  PowerOff,
  Play,
  ChevronDown,
  ChevronUp,
  X,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Pencil,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import workflowService, {
  TriggerType,
  ActionType,
  ConditionOperator,
  AutomationRule,
  AutomationExecution,
  CreateAutomationRuleDto,
  Action,
  ExecutionStatus,
  TestRuleResult,
} from '@/services/workflow.service';
import ActionConfigPanel, { getDefaultConfig, getMissingConfigFields } from './ActionConfigPanel';
import ConditionBuilder from './ConditionBuilder';
import { VisualWorkflowBuilder } from './VisualWorkflowBuilder';
import { useOrgTicketTypes } from './VisualWorkflowBuilder/hooks/useOrgData';
import { ExecutionErrorDetails } from './ExecutionErrorDetails';
import { ExecutionProgressBar } from './ExecutionProgressBar';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Trigger type display labels
const TRIGGER_LABELS: Record<TriggerType, string> = {
  [TriggerType.TICKET_CREATED]: 'Ticket Created',
  [TriggerType.TICKET_UPDATED]: 'Ticket Updated',
  [TriggerType.TICKET_STATUS_CHANGED]: 'Status Changed',
  [TriggerType.TICKET_ASSIGNED]: 'Ticket Assigned',
  [TriggerType.TICKET_PRIORITY_CHANGED]: 'Priority Changed',
  [TriggerType.TICKET_SLA_BREACH]: 'SLA Breach',
  [TriggerType.MESSAGE_RECEIVED]: 'Customer Replies',
  [TriggerType.MESSAGE_SENT]: 'Agent Replies',
  [TriggerType.CUSTOMER_CREATED]: 'Customer Created',
  [TriggerType.CUSTOMER_UPDATED]: 'Customer Updated',
  [TriggerType.CHAT_STARTED]: 'Chat Started',
  [TriggerType.CHAT_ENDED]: 'Chat Ended',
  [TriggerType.CHAT_IDLE]: 'Chat Idle',
  [TriggerType.EMAIL_RECEIVED]: 'Email Received',
  [TriggerType.SCHEDULED]: 'Scheduled',
  [TriggerType.MANUAL]: 'Manual',
  [TriggerType.WEBHOOK]: 'Webhook',
  [TriggerType.LINE_ITEM_QUOTE_VALIDATION_FAILED]: 'Quote Validation Failed',
};

/**
 * Label for a trigger whatever its case: the API returns UPPERCASE values
 * (MESSAGE_RECEIVED) while TRIGGER_LABELS is keyed by this editor's lowercase
 * enum, so a direct lookup missed and lists showed raw or empty labels.
 */
export function triggerLabelOf(triggerType: string | null | undefined): string | undefined {
  if (!triggerType) return undefined;
  return TRIGGER_LABELS[String(triggerType).toLowerCase() as TriggerType];
}

const ACTION_LABELS: Record<ActionType, string> = {
  [ActionType.SET_STATUS]: 'Set Status',
  [ActionType.SET_PRIORITY]: 'Set Priority',
  [ActionType.ASSIGN_AGENT]: 'Assign Agent',
  [ActionType.ASSIGN_TEAM]: 'Assign Team',
  [ActionType.AUTO_ASSIGN]: 'Auto-assign Agent',
  [ActionType.ADD_TAG]: 'Add Tag',
  [ActionType.REMOVE_TAG]: 'Remove Tag',
  [ActionType.SEND_EMAIL]: 'Send Email',
  [ActionType.SEND_NOTIFICATION]: 'Send Notification',
  [ActionType.TRIGGER_WEBHOOK]: 'Trigger Webhook',
  [ActionType.TRIGGER_N8N_WORKFLOW]: 'n8n Workflow',
  [ActionType.CREATE_TICKET]: 'Create Ticket',
  // Branching/Control flow
  [ActionType.CONDITIONAL]: 'If/Else Condition',
  [ActionType.SWITCH]: 'Switch/Case',
  [ActionType.STOP_WORKFLOW]: 'Stop Workflow',
};

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  [ConditionOperator.EQUALS]: 'equals',
  [ConditionOperator.NOT_EQUALS]: 'does not equal',
  [ConditionOperator.CONTAINS]: 'contains',
  [ConditionOperator.NOT_CONTAINS]: 'does not contain',
  [ConditionOperator.STARTS_WITH]: 'starts with',
  [ConditionOperator.ENDS_WITH]: 'ends with',
  [ConditionOperator.GREATER_THAN]: 'greater than',
  [ConditionOperator.LESS_THAN]: 'less than',
  [ConditionOperator.IN]: 'is in list',
  [ConditionOperator.NOT_IN]: 'is not in list',
  [ConditionOperator.IS_EMPTY]: 'is empty',
  [ConditionOperator.IS_NOT_EMPTY]: 'is not empty',
};

const STATUS_COLORS: Record<ExecutionStatus, string> = {
  [ExecutionStatus.PENDING]: 'bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600',
  [ExecutionStatus.RUNNING]: 'bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
  [ExecutionStatus.COMPLETED]: 'bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800',
  [ExecutionStatus.FAILED]: 'bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800',
  [ExecutionStatus.SKIPPED]: 'bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
};

const STATUS_ICONS: Record<ExecutionStatus, React.ReactNode> = {
  [ExecutionStatus.PENDING]: <Clock className="w-3 h-3" />,
  [ExecutionStatus.RUNNING]: <Loader2 className="w-3 h-3 animate-spin" />,
  [ExecutionStatus.COMPLETED]: <CheckCircle className="w-3 h-3" />,
  [ExecutionStatus.FAILED]: <XCircle className="w-3 h-3" />,
  [ExecutionStatus.SKIPPED]: <AlertCircle className="w-3 h-3" />,
};

function getErrorMessage(error: any): string {
  const data = error?.response?.data;
  if (data?.message) {
    return Array.isArray(data.message) ? data.message.join(', ') : data.message;
  }
  return error?.message || 'An unexpected error occurred';
}

/**
 * Returns a human-readable configuration error for a rule, or null when every
 * action is valid. A rule with a misconfigured action is in an error state and
 * must not be enabled until it is fixed. Mirrors the validation in
 * handleCreateRule so the list and the editor agree on what "broken" means.
 */
/** A Visual Builder (node-graph) rule — its blocks, not `actions`, are what runs. */
function isNodeWorkflow(rule: AutomationRule): boolean {
  return (rule.schemaVersion === 2 || rule.workflow?.version === '2.0') && Array.isArray(rule.workflow?.nodes);
}

/** Event types of a rule's Trigger blocks (empty for non-Visual-Builder rules). */
export function workflowTriggerTypes(rule: AutomationRule): string[] {
  if (!isNodeWorkflow(rule)) return [];
  return [
    ...new Set(
      (rule.workflow?.nodes ?? [])
        .filter((n) => n.type === 'trigger')
        .map((n) => n.config?.eventType)
        .filter((t): t is string => typeof t === 'string' && t.length > 0),
    ),
  ];
}

/** True when a workflow starts on more than one trigger (Visual Builder only). */
export function isMultiTriggerRule(rule: AutomationRule): boolean {
  return workflowTriggerTypes(rule).length > 1;
}

/**
 * The list line under a rule's name: its trigger(s) and size. A Visual Builder
 * workflow shows every trigger it starts on and counts its action blocks — its
 * `actions` array is empty, so it used to read "0 action(s)".
 */
export function ruleSummary(rule: AutomationRule): string {
  if (isNodeWorkflow(rule)) {
    const triggers = workflowTriggerTypes(rule);
    const labels = (triggers.length > 0 ? triggers : [rule.triggerType]).map((t) => triggerLabelOf(t) ?? t);
    const actions = (rule.workflow?.nodes ?? []).filter((n) => n.type === 'action').length;
    return [labels.join(' / '), `${actions} action block(s)`].join(' • ');
  }
  return [triggerLabelOf(rule.triggerType), `${rule.actions.length} action(s)`].filter(Boolean).join(' • ');
}

export function getRuleConfigError(rule: AutomationRule): string | null {
  for (const action of rule.actions) {
    const missing = getMissingConfigFields(action);
    if (missing.length > 0) {
      const actionLabel = ACTION_LABELS[action.type] || action.type;
      return `${actionLabel}: ${missing.join(', ')} required`;
    }
  }
  return null;
}

// Field label map for human-readable condition display
const FIELD_LABELS: Record<string, string> = {
  'entity.ticketTypeId': 'Ticket type',
  'entity.priority': 'Priority',
  'entity.status': 'Status',
  'entity.channel': 'Channel',
  'entity.tags': 'Tags',
  'entity.assigneeId': 'Assignee',
  'entity.teamId': 'Team',
  'entity.customerId': 'Customer',
};

export function WorkflowAutomation() {
  const toast = useToast();
  const { ticketTypes } = useOrgTicketTypes();
  const [activeTab, setActiveTab] = useState<'rules' | 'executions'>('rules');
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [executions, setExecutions] = useState<AutomationExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [executionSearch, setExecutionSearch] = useState('');
  const [executionStatusFilter, setExecutionStatusFilter] = useState<ExecutionStatus | 'all'>('all');
  const [executionPage, setExecutionPage] = useState(1);
  const [executionPageSize, setExecutionPageSize] = useState(25);
  const [executionTotal, setExecutionTotal] = useState(0);
  const [executionTotalPages, setExecutionTotalPages] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [builderMode, setBuilderMode] = useState<'visual' | 'advanced'>('visual');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<AutomationRule | null>(null);
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState<{ total: number; active: number } | null>(null);
  const [executionStats, setExecutionStats] = useState<{
    total: number;
    completed: number;
    failed: number;
    pending: number;
    running: number;
  } | null>(null);
  const [testingRuleId, setTestingRuleId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestRuleResult>>({});
  const [renamingRuleId, setRenamingRuleId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [expandedExecutions, setExpandedExecutions] = useState<Set<string>>(new Set());
  const [isBuilderFullscreen, setIsBuilderFullscreen] = useState(true);

  // Form state
  const [formData, setFormData] = useState<CreateAutomationRuleDto>({
    name: '',
    description: '',
    triggerType: TriggerType.TICKET_CREATED,
    triggerConditions: { logic: 'AND', conditions: [] },
    actions: [],
    isActive: true,
    stopOnMatch: false,
  });

  useEffect(() => {
    loadData();
  }, [activeTab, executionStatusFilter, executionPage, executionPageSize]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'rules') {
        const [rulesResponse, statsResponse] = await Promise.all([
          workflowService.getRules({ limit: 50 }),
          workflowService.getRuleStats(),
        ]);
        setRules(rulesResponse.data || []);
        setStats(statsResponse);
      } else {
        // Load both executions AND rules (to show workflow names)
        const [execResponse, execStatsResponse, rulesResponse] = await Promise.all([
          workflowService.getExecutions({ page: executionPage, limit: executionPageSize, status: executionStatusFilter === 'all' ? undefined : executionStatusFilter }),
          workflowService.getExecutionStats(),
          workflowService.getRules({ limit: 100 }),
        ]);
        setExecutions(execResponse.data || []);
        setExecutionTotal(execResponse.total || 0);
        setExecutionTotalPages(execResponse.totalPages || 0);
        setExecutionStats(execStatsResponse);
        setRules(rulesResponse.data || []);
      }
    } catch {
      toast.error('Error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Changing the status filter always returns to the first page so the user
  // isn't stranded on a page number that no longer exists for the new filter.
  const changeExecutionStatus = (status: ExecutionStatus | 'all') => {
    setExecutionStatusFilter(status);
    setExecutionPage(1);
  };

  const toggleExpand = (ruleId: string) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(ruleId)) {
      newExpanded.delete(ruleId);
    } else {
      newExpanded.add(ruleId);
    }
    setExpandedRules(newExpanded);
  };

  const toggleExecutionExpand = (executionId: string) => {
    const newExpanded = new Set(expandedExecutions);
    if (newExpanded.has(executionId)) {
      newExpanded.delete(executionId);
    } else {
      newExpanded.add(executionId);
    }
    setExpandedExecutions(newExpanded);
  };

  const handleToggleRule = async (rule: AutomationRule) => {
    try {
      await workflowService.toggleRule(rule.id, !rule.isActive);
      toast.success('Success', `Rule ${rule.isActive ? 'disabled' : 'enabled'}`);
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    }
  };

  const handleDeleteRule = async (rule: AutomationRule) => {
    if (!confirm(`Are you sure you want to delete "${rule.name}"?`)) {
      return;
    }
    try {
      await workflowService.deleteRule(rule.id);
      toast.success('Success', 'Rule deleted');
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    }
  };

  const handleRename = async (ruleId: string, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setRenamingRuleId(null);
      return;
    }
    try {
      await workflowService.updateRule(ruleId, { name: trimmed } as any);
      setRules((prev) => prev.map((r) => (r.id === ruleId ? { ...r, name: trimmed } : r)));
      toast.success('Renamed', 'Rule renamed successfully');
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    }
    setRenamingRuleId(null);
  };

  const handleDuplicateRule = async (rule: AutomationRule) => {
    try {
      await workflowService.duplicateRule(rule.id, `${rule.name} (Copy)`);
      toast.success('Success', 'Rule duplicated');
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    }
  };

  const handleTestRule = async (rule: AutomationRule) => {
    try {
      setTestingRuleId(rule.id);
      // Build smart sample data from rule conditions so the test is meaningful
      const sampleData: Record<string, unknown> = {
        status: 'in_progress',
        priority: 'high',
        channel: 'email',
        subject: 'Sample ticket for testing',
        customerEmail: 'test@example.com',
        tags: ['sample'],
      };
      // Pre-fill fields from rule conditions so the condition evaluator can match
      const conditions = (rule.triggerConditions as any)?.conditions ?? [];
      for (const cond of conditions) {
        if (!cond.field || !cond.value) continue;
        const field = (cond.field as string);
        // Strip 'entity.' prefix since sampleData is the entity itself
        const key = field.startsWith('entity.') ? field.slice(7) : field;
        sampleData[key] = cond.value;
      }
      const result = await workflowService.testRule(rule.id, sampleData);
      setTestResults((prev) => ({ ...prev, [rule.id]: result }));
      // Auto-expand the rule to show results
      setExpandedRules((prev) => new Set([...prev, rule.id]));
      if (result.matched) {
        toast.success('Test Passed', `Rule "${rule.name}" matched! ${rule.actions.length} action(s) would execute.`);
      } else {
        toast.error('Test Failed', `Rule "${rule.name}" did not match. Check conditions.`);
      }
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    } finally {
      setTestingRuleId(null);
    }
  };

  const handleCreateRule = async () => {
    if (!formData.name) {
      toast.error('Validation Error', 'Name is required');
      return;
    }
    if (formData.actions.length === 0) {
      toast.error('Validation Error', 'At least one action is required');
      return;
    }

    // Validate each action has required config fields
    for (const action of formData.actions) {
      const missing = getMissingConfigFields(action);
      if (missing.length > 0) {
        const actionLabel = ACTION_LABELS[action.type] || action.type;
        toast.error('Validation Error', `${actionLabel}: ${missing.join(', ')} required`);
        return;
      }
    }

    try {
      setSaving(true);
      await workflowService.createRule(formData);
      toast.success('Success', 'Automation rule created');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRule = async () => {
    if (!selectedRule) return;
    if (isMultiTriggerRule(selectedRule)) {
      toast.error('Edit in the Visual Builder', 'This workflow starts on several triggers, which the Advanced form cannot keep.');
      return;
    }

    try {
      setSaving(true);
      await workflowService.updateRule(selectedRule.id, formData);
      toast.success('Success', 'Automation rule updated');
      setShowEditModal(false);
      loadData();
    } catch (error: any) {
      toast.error('Error', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (rule: AutomationRule) => {
    setSelectedRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      // The API returns UPPERCASE (MESSAGE_RECEIVED); this editor's options are
      // lowercase, so normalise or the trigger dropdown shows nothing selected.
      triggerType: String(rule.triggerType).toLowerCase() as TriggerType,
      triggerConditions: rule.triggerConditions,
      actions: rule.actions,
      isActive: rule.isActive,
      stopOnMatch: rule.stopOnMatch,
    });
    setBuilderMode('visual');
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      triggerType: TriggerType.TICKET_CREATED,
      triggerConditions: { logic: 'AND', conditions: [] },
      actions: [],
      isActive: true,
      stopOnMatch: false,
    });
  };

  const addAction = () => {
    const defaultType = ActionType.SET_STATUS;
    setFormData({
      ...formData,
      actions: [
        ...formData.actions,
        { type: defaultType, config: getDefaultConfig(defaultType) },
      ],
    });
  };

  const removeAction = (index: number) => {
    const newActions = [...formData.actions];
    newActions.splice(index, 1);
    setFormData({ ...formData, actions: newActions });
  };

  /** Resolve a condition field + value to human-readable text */
  const resolveConditionDisplay = (field: string, value: unknown): { fieldLabel: string; valueLabel: string } => {
    const fieldLabel = FIELD_LABELS[field] || field;
    let valueLabel = String(value ?? '');

    if (field === 'entity.ticketTypeId' && typeof value === 'string') {
      valueLabel = ticketTypes.find((t) => t.id === value)?.name ?? valueLabel;
    }

    return { fieldLabel, valueLabel };
  };

  const updateAction = (index: number, action: Action) => {
    const newActions = [...formData.actions];
    newActions[index] = action;
    setFormData({ ...formData, actions: newActions });
  };

  const filteredRules = rules.filter(
    (rule) =>
      rule.name.toLowerCase().includes(search.toLowerCase()) ||
      rule.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Helper: Get rule name by ruleId
  const getRuleName = (ruleId: string): string => {
    const rule = rules.find((r) => r.id === ruleId);
    return rule?.name || 'Unknown Workflow';
  };

  // Helper: Get ticket number from execution (check triggerPayload for ticketNumber)
  const getTicketDisplay = (execution: AutomationExecution): string => {
    const payload = execution.triggerPayload as Record<string, unknown> | undefined;
    // Check for ticketNumber in payload (common field name)
    if (payload?.ticketNumber) {
      return String(payload.ticketNumber);
    }
    // Check for ticket object with number
    if (payload?.ticket && typeof payload.ticket === 'object') {
      const ticket = payload.ticket as Record<string, unknown>;
      if (ticket.number) return String(ticket.number);
      if (ticket.ticketNumber) return String(ticket.ticketNumber);
    }
    // Check entity snapshot for number
    if (payload?.entity && typeof payload.entity === 'object') {
      const entity = payload.entity as Record<string, unknown>;
      if (entity.number) return String(entity.number);
      if (entity.ticketNumber) return String(entity.ticketNumber);
    }
    // Fallback: show truncated ID
    return execution.entityId?.slice(0, 8) || '-';
  };

  // Filter executions based on search (by workflow name, status, entity)
  const filteredExecutions = executions.filter((execution) => {
    if (!executionSearch) return true;
    const searchLower = executionSearch.toLowerCase();
    const ruleName = getRuleName(execution.ruleId).toLowerCase();
    const status = execution.status.toLowerCase();
    const entityDisplay = getTicketDisplay(execution).toLowerCase();
    const triggerLabel = triggerLabelOf(execution.triggerType)?.toLowerCase() || '';
    return (
      ruleName.includes(searchLower) ||
      status.includes(searchLower) ||
      entityDisplay.includes(searchLower) ||
      triggerLabel.includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Workflow Automation
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Create automation rules to streamline your workflow
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Rule
        </Button>
      </div>

      {/* Stats */}
      {stats && activeTab === 'rules' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Rules</p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-white">
              {stats.total}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Active Rules</p>
            <p className="text-2xl font-semibold text-green-600 dark:text-green-400">
              {stats.active}
            </p>
          </div>
        </div>
      )}

      {executionStats && activeTab === 'executions' && (
        <div className="grid grid-cols-4 gap-4">
          {([
            { key: 'all' as const, label: 'Total', value: executionStats.total, valueClass: 'text-gray-900 dark:text-white' },
            { key: ExecutionStatus.COMPLETED, label: 'Completed', value: executionStats.completed, valueClass: 'text-green-600' },
            { key: ExecutionStatus.FAILED, label: 'Failed', value: executionStats.failed, valueClass: 'text-red-600' },
            { key: ExecutionStatus.RUNNING, label: 'Running', value: executionStats.running, valueClass: 'text-blue-600' },
          ]).map((card) => {
            const active = executionStatusFilter === card.key;
            return (
              <button
                key={card.label}
                type="button"
                onClick={() => changeExecutionStatus(card.key)}
                aria-pressed={active}
                title={card.key === 'all' ? 'Show all executions' : `Filter by ${card.label}`}
                className={`text-left bg-white dark:bg-gray-800 rounded-lg border p-4 transition-colors hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  active ? 'border-primary-500 ring-2 ring-primary-500' : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
                <p className={`text-2xl font-semibold ${card.valueClass}`}>{card.value}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('rules')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'rules'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Rules
          </button>
          <button
            onClick={() => setActiveTab('executions')}
            className={`py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'executions'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Play className="w-4 h-4 inline mr-2" />
            Execution Log
          </button>
        </nav>
      </div>

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <Input
              placeholder="Search rules..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Rules List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : filteredRules.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <Zap className="w-12 h-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No automation rules
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Create your first rule to automate workflows
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Rule
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRules.map((rule) => {
                const configError = getRuleConfigError(rule);
                return (
                <div
                  key={rule.id}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  {/* Rule Header */}
                  <div
                    className="flex items-center justify-between gap-3 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={() => toggleExpand(rule.id)}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`w-2 h-2 flex-shrink-0 rounded-full ${
                          configError ? 'bg-red-500' : rule.isActive ? 'bg-green-500' : 'bg-gray-400'
                        }`}
                      />
                      {configError && (
                        <span
                          className="flex-shrink-0"
                          title={`Workflow has an error: ${configError}`}
                        >
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        </span>
                      )}
                      <div>
                        {renamingRuleId === rule.id ? (
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => handleRename(rule.id, renameValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRename(rule.id, renameValue);
                              if (e.key === 'Escape') setRenamingRuleId(null);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full text-sm font-medium border-b-2 border-primary-500 bg-transparent outline-none text-gray-900 dark:text-white"
                          />
                        ) : (
                          <h4 className="group/name flex items-center text-sm font-medium text-gray-900 dark:text-white">
                            {rule.name}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenamingRuleId(rule.id);
                                setRenameValue(rule.name);
                              }}
                              title="Rename"
                              className="ml-1.5 opacity-0 group-hover/name:opacity-100 transition-opacity"
                            >
                              <Pencil className="w-3 h-3 text-gray-400 hover:text-primary-600 dark:text-gray-500" />
                            </button>
                          </h4>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {ruleSummary(rule)}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (configError) {
                            toast.error('Workflow has an error', configError);
                            return;
                          }
                          handleToggleRule(rule);
                        }}
                        aria-disabled={configError ? true : undefined}
                        className={`p-1 ${
                          configError
                            ? 'text-red-400 cursor-not-allowed dark:text-red-500'
                            : rule.isActive
                            ? 'text-green-600 hover:text-green-700'
                            : 'text-gray-400 hover:text-gray-600 dark:text-gray-500'
                        }`}
                        title={
                          configError
                            ? `Cannot enable — fix the error first: ${configError}`
                            : rule.isActive
                            ? 'Disable'
                            : 'Enable'
                        }
                      >
                        {rule.isActive ? (
                          <Power className="w-4 h-4" />
                        ) : (
                          <PowerOff className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(rule);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 dark:text-gray-500"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateRule(rule);
                        }}
                        className="p-1 text-gray-400 hover:text-purple-600 dark:text-gray-500"
                        title="Duplicate"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTestRule(rule);
                        }}
                        className="p-1 text-gray-400 hover:text-green-600 dark:text-gray-500"
                        title="Test Rule"
                        disabled={testingRuleId === rule.id}
                      >
                        {testingRuleId === rule.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRule(rule);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedRules.has(rule.id) ? (
                        <ChevronUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedRules.has(rule.id) && (
                    <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700">
                      {rule.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {rule.description}
                        </p>
                      )}

                      {/* Conditions */}
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Conditions ({rule.triggerConditions.logic})
                        </p>
                        {rule.triggerConditions.conditions.length === 0 ? (
                          <p className="text-xs text-gray-400 dark:text-gray-500">No conditions - always triggers</p>
                        ) : (
                          <div className="space-y-1">
                            {rule.triggerConditions.conditions.map((condition, i) => {
                              const { fieldLabel, valueLabel } = resolveConditionDisplay(condition.field, condition.value);
                              return (
                                <div key={i} className="text-xs text-gray-600 dark:text-gray-400">
                                  {fieldLabel} {OPERATOR_LABELS[condition.operator]}{' '}
                                  {condition.value !== undefined && (
                                    <span className="font-medium">"{valueLabel}"</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          Actions
                        </p>
                        <div className="space-y-1">
                          {rule.actions.map((action, i) => (
                            <div
                              key={i}
                              className="text-xs bg-gray-50 dark:bg-gray-700 rounded px-2 py-1 text-gray-600 dark:text-gray-400"
                            >
                              <span className="font-medium">{ACTION_LABELS[action.type]}</span>
                              {action.delay && (
                                <span className="text-gray-400 ml-2 dark:text-gray-500">
                                  (delay: {action.delay}s)
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Test Results */}
                      {testResults[rule.id] && (
                        <div className={`mt-3 p-3 rounded-lg ${
                          testResults[rule.id].matched
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-medium text-gray-900 dark:text-white">
                              Test Result: {testResults[rule.id].matched ? 'Matched' : 'Not Matched'}
                            </p>
                            <button
                              onClick={() => setTestResults((prev) => {
                                const next = { ...prev };
                                delete next[rule.id];
                                return next;
                              })}
                              className="text-gray-400 hover:text-gray-600 dark:text-gray-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          {testResults[rule.id].conditionResults.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              No conditions — rule matches all events of this trigger type.
                            </p>
                          ) : (
                            <div className="space-y-1">
                              {testResults[rule.id].conditionResults.map((cr, i) => {
                                const { fieldLabel, valueLabel: expectedLabel } = resolveConditionDisplay(cr.field, cr.expected);
                                const { valueLabel: actualLabel } = resolveConditionDisplay(cr.field, cr.actual);
                                return (
                                  <div key={i} className="text-xs text-gray-600 dark:text-gray-400">
                                    <span className={cr.result ? 'text-green-600' : 'text-red-600'}>
                                      {cr.result ? 'PASS' : 'FAIL'}
                                    </span>
                                    {' '}{fieldLabel} {cr.operator} "{expectedLabel}"
                                    <span className="text-gray-400 ml-1 dark:text-gray-500">
                                      (actual: {actualLabel || 'empty'})
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Executions Tab */}
      {activeTab === 'executions' && (
        <>
          {/* Search + status filter for executions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <Input
                placeholder="Search by workflow name, status, ticket..."
                value={executionSearch}
                onChange={(e) => setExecutionSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <ThemedSelect
              value={executionStatusFilter}
              onChange={(e) => changeExecutionStatus(e.target.value as ExecutionStatus | 'all')}
              title="Filter by status"
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All statuses</option>
              <option value={ExecutionStatus.COMPLETED}>Completed</option>
              <option value={ExecutionStatus.FAILED}>Failed</option>
              <option value={ExecutionStatus.RUNNING}>Running</option>
              <option value={ExecutionStatus.PENDING}>Pending</option>
              <option value={ExecutionStatus.SKIPPED}>Skipped</option>
            </ThemedSelect>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : filteredExecutions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <Play className="w-12 h-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {executionStatusFilter !== 'all' || executionSearch ? 'No matching executions' : 'No executions yet'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {executionStatusFilter !== 'all' || executionSearch
                  ? 'No executions match the current filters. Try clearing the status filter or search.'
                  : 'Executions will appear here when automation rules are triggered'}
              </p>
              {(executionStatusFilter !== 'all' || executionSearch) && (
                <button
                  type="button"
                  onClick={() => { changeExecutionStatus('all'); setExecutionSearch(''); }}
                  className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* The inner overflow-x-auto is what makes the trailing columns
                  reachable: every cell here is nowrap, so the table easily
                  exceeds the panel width, and the rounded clipping wrapper on
                  its own just cut the last column (Duration) off with no way to
                  scroll to it. */}
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="w-8 px-2"></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Workflow
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Trigger
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Ticket
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Progress
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Started
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                      Duration
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredExecutions.map((execution) => {
                    const isExpanded = expandedExecutions.has(execution.id);
                    const hasFailed = execution.status === ExecutionStatus.FAILED;
                    const hasStructuredError = execution.structuredError != null;
                    // #9154: any FAILED run is clickable/expandable so the user can always drill
                    // into why it failed; the panel shows a fallback if no error was recorded.
                    const isExpandable = hasFailed;

                    return (
                      <React.Fragment key={execution.id}>
                        <tr
                          className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${isExpandable ? 'cursor-pointer' : ''}`}
                          onClick={() => isExpandable && toggleExecutionExpand(execution.id)}
                        >
                          <td className="px-2 py-3 text-center">
                            {isExpandable && (
                              isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-400 dark:text-gray-500 inline" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500 inline" />
                              )
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white max-w-[200px] truncate" title={getRuleName(execution.ruleId)}>
                            {getRuleName(execution.ruleId)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                                STATUS_COLORS[execution.status]
                              }`}
                            >
                              {STATUS_ICONS[execution.status]}
                              {execution.status.charAt(0) + execution.status.slice(1).toLowerCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {triggerLabelOf(execution.triggerType) || execution.triggerType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {execution.entityType && (
                              <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                                {getTicketDisplay(execution)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {execution.structuredError ? (
                              <div className="flex items-center gap-2">
                                <div className="w-20">
                                  <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${
                                        execution.status === ExecutionStatus.COMPLETED
                                          ? 'bg-green-500'
                                          : execution.status === ExecutionStatus.FAILED
                                          ? 'bg-red-500'
                                          : 'bg-blue-500'
                                      }`}
                                      style={{
                                        width: `${
                                          execution.structuredError.totalActionsCount > 0
                                            ? (execution.structuredError.completedActionsCount /
                                                execution.structuredError.totalActionsCount) *
                                              100
                                            : 0
                                        }%`,
                                      }}
                                    />
                                  </div>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {execution.structuredError.completedActionsCount}/
                                  {execution.structuredError.totalActionsCount}
                                </span>
                              </div>
                            ) : execution.actionsExecuted ? (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {execution.actionsExecuted.length} actions
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {execution.startedAt
                              ? new Date(execution.startedAt).toLocaleString()
                              : '-'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {execution.startedAt && execution.completedAt
                              ? `${(
                                  (new Date(execution.completedAt).getTime() -
                                    new Date(execution.startedAt).getTime()) /
                                  1000
                                ).toFixed(2)}s`
                              : '-'}
                          </td>
                        </tr>
                        {/* Expanded row for error details */}
                        {isExpanded && isExpandable && (
                          <tr>
                            <td colSpan={8} className="px-4 py-4 bg-gray-50 dark:bg-gray-800/50">
                              {hasStructuredError && execution.structuredError ? (
                                <ExecutionErrorDetails
                                  structuredError={execution.structuredError}
                                  actionLabels={ACTION_LABELS}
                                  defaultExpanded={false}
                                />
                              ) : execution.errorMessage ? (
                                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                  <div className="flex items-start gap-3">
                                    <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                        Execution Failed
                                      </p>
                                      <pre className="mt-2 text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap break-words max-h-40 overflow-auto">
                                        {execution.errorMessage}
                                      </pre>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                  <div className="flex items-start gap-3">
                                    <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                        Execution Failed
                                      </p>
                                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                        No detailed error was recorded for this run.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {executionTotal > 0 && (
            <Pagination
              currentPage={executionPage}
              totalPages={executionTotalPages}
              totalItems={executionTotal}
              pageSize={executionPageSize}
              onPageChange={(pg) => setExecutionPage(pg)}
              onPageSizeChange={(size) => { setExecutionPageSize(size); setExecutionPage(1); }}
              pageSizeOptions={[10, 25, 50, 100]}
              className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700"
            />
          )}
        </>
      )}

      {/* Create Modal — Visual Builder or Advanced form.
          Portaled to document.body so the fixed overlay covers the full viewport
          even though an ancestor in the org-settings tree has a transform/filter
          (which would otherwise make `fixed` position relative to it). */}
      {showCreateModal && createPortal(
        <div className={`fixed z-50 flex flex-col bg-white dark:bg-gray-900 transition-all duration-300 ${
          isBuilderFullscreen
            ? 'inset-0'
            : 'inset-x-4 inset-y-4 md:inset-x-[10%] md:inset-y-[5%] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700'
        }`}>
          {/* Modal header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">New Automation Rule</h3>
              {/* Mode toggle */}
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setBuilderMode('visual')}
                  className={`rounded-l-lg px-3 py-1 text-xs font-medium transition-colors ${
                    builderMode === 'visual'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  🎨 Visual Builder
                </button>
                <button
                  onClick={() => setBuilderMode('advanced')}
                  className={`rounded-r-lg px-3 py-1 text-xs font-medium transition-colors ${
                    builderMode === 'advanced'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  ⚙️ Advanced
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Fullscreen toggle */}
              <button
                onClick={() => setIsBuilderFullscreen(!isBuilderFullscreen)}
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:text-gray-500"
                title={isBuilderFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isBuilderFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Builder area — both views stay mounted (only visibility toggles) so
              switching Visual <-> Advanced never discards in-progress work; the
              visual canvas used to reset to TRIGGER->End on every toggle (#9127). */}
          <div className="flex-1 overflow-hidden p-4">
            <div className={builderMode === 'visual' ? 'h-full' : 'hidden'}>
              <VisualWorkflowBuilder
                ruleName={formData.name || 'New Workflow'}
                onSave={async (rules) => {
                  if (!rules.length) return;
                  // Save each generated rule
                  for (const rule of rules) {
                    await workflowService.createRule(rule as any);
                  }
                  setShowCreateModal(false);
                  resetForm();
                  loadData();
                  toast.success('Saved', `${rules.length} workflow rule${rules.length > 1 ? 's' : ''} created`);
                }}
              />
            </div>
            <div className={builderMode === 'advanced' ? 'mx-auto max-w-2xl' : 'hidden'}>
              <RuleModal
                title=""
                formData={formData}
                setFormData={setFormData}
                onClose={() => { setShowCreateModal(false); resetForm(); }}
                onSave={handleCreateRule}
                saving={saving}
                addAction={addAction}
                removeAction={removeAction}
                updateAction={updateAction}
                inline
              />
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Edit Modal — Visual Builder or Advanced form.
          Portaled to document.body for the same reason as the Create modal above. */}
      {showEditModal && selectedRule && createPortal(
        <div className={`fixed z-50 flex flex-col bg-white dark:bg-gray-900 transition-all duration-300 ${
          isBuilderFullscreen
            ? 'inset-0'
            : 'inset-x-4 inset-y-4 md:inset-x-[10%] md:inset-y-[5%] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700'
        }`}>
          {/* Modal header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Edit Automation Rule</h3>
              {/* Mode toggle */}
              <div className="flex rounded-lg border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setBuilderMode('visual')}
                  className={`rounded-l-lg px-3 py-1 text-xs font-medium transition-colors ${
                    builderMode === 'visual'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  Visual Builder
                </button>
                <button
                  onClick={() => setBuilderMode('advanced')}
                  // The Advanced form has one trigger and a flat action list; it
                  // cannot represent several triggers and would overwrite them.
                  disabled={isMultiTriggerRule(selectedRule)}
                  title={
                    isMultiTriggerRule(selectedRule)
                      ? 'This workflow starts on several triggers — edit it in the Visual Builder'
                      : undefined
                  }
                  className={`rounded-r-lg px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    builderMode === 'advanced'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  Advanced
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Fullscreen toggle */}
              <button
                onClick={() => setIsBuilderFullscreen(!isBuilderFullscreen)}
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:text-gray-500"
                title={isBuilderFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              >
                {isBuilderFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Builder area — both views stay mounted (only visibility toggles) so
              switching Visual <-> Advanced never discards in-progress edits (#9127). */}
          <div className="flex-1 overflow-hidden p-4">
            <div className={builderMode === 'visual' ? 'h-full' : 'hidden'}>
              <VisualWorkflowBuilder
                ruleName={selectedRule.name}
                initialRule={selectedRule}
                onSave={async (rules) => {
                  if (!rules.length) return;
                  await workflowService.updateRule(selectedRule.id, rules[0] as any);
                  setShowEditModal(false);
                  loadData();
                  toast.success('Saved', 'Workflow rule updated');
                }}
              />
            </div>
            <div className={builderMode === 'advanced' ? 'mx-auto max-w-2xl' : 'hidden'}>
              <RuleModal
                title=""
                formData={formData}
                setFormData={setFormData}
                onClose={() => setShowEditModal(false)}
                onSave={handleUpdateRule}
                saving={saving}
                addAction={addAction}
                removeAction={removeAction}
                updateAction={updateAction}
                inline
              />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// Modal component for create/edit
function RuleModal({
  title,
  formData,
  setFormData,
  onClose,
  onSave,
  saving,
  addAction,
  removeAction,
  updateAction,
  inline = false,
}: {
  title: string;
  formData: CreateAutomationRuleDto;
  setFormData: (data: CreateAutomationRuleDto) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  addAction: () => void;
  removeAction: (index: number) => void;
  updateAction: (index: number, action: Action) => void;
  inline?: boolean;
}) {
  const [showTriggerDropdown, setShowTriggerDropdown] = useState(false);
  const [openActionDropdown, setOpenActionDropdown] = useState<number | null>(null);

  const inner = (
    <>
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800 rounded-t-lg">
          {title && <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>}
          {!inline && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:text-gray-500">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Auto-assign high priority tickets"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What does this automation do?"
                rows={2}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Trigger */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Trigger Event *
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTriggerDropdown(!showTriggerDropdown)}
                className="w-full flex items-center justify-between rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
              >
                <span>{triggerLabelOf(formData.triggerType)}</span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showTriggerDropdown ? 'rotate-180' : ''} dark:text-gray-500`} />
              </button>
              {showTriggerDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowTriggerDropdown(false)} />
                  <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, triggerType: value as TriggerType });
                          setShowTriggerDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                          formData.triggerType === value
                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                            : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Conditions */}
          <ConditionBuilder
            conditionGroup={formData.triggerConditions}
            onChange={(conditionGroup) => setFormData({ ...formData, triggerConditions: conditionGroup })}
            triggerType={formData.triggerType}
          />

          {/* Actions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Actions *
              </label>
              <Button variant="outline" size="sm" onClick={addAction}>
                <Plus className="w-3 h-3 mr-1" />
                Add Action
              </Button>
            </div>

            {formData.actions.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                No actions added yet
              </p>
            ) : (
              <div className="space-y-2">
                {formData.actions.map((action, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <button
                          type="button"
                          onClick={() => setOpenActionDropdown(openActionDropdown === index ? null : index)}
                          className="w-full flex items-center justify-between rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-3 py-1.5 text-sm text-gray-900 dark:text-white"
                        >
                          <span>{ACTION_LABELS[action.type]}</span>
                          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${openActionDropdown === index ? 'rotate-180' : ''} dark:text-gray-500`} />
                        </button>
                        {openActionDropdown === index && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenActionDropdown(null)} />
                            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {Object.entries(ACTION_LABELS).map(([value, label]) => (
                                <button
                                  key={value}
                                  type="button"
                                  onClick={() => {
                                    const newType = value as ActionType;
                                    updateAction(index, { type: newType, config: getDefaultConfig(newType) });
                                    setOpenActionDropdown(null);
                                  }}
                                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-500 ${
                                    action.type === value
                                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                      : 'text-gray-900 dark:text-white'
                                  }`}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => removeAction(index)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <ActionConfigPanel
                      action={action}
                      onChange={(config) => updateAction(index, { ...action, config })}
                      onConditionalChange={(conditional) => updateAction(index, { ...action, conditional })}
                      onSwitchChange={(switchConfig) => updateAction(index, { ...action, switch: switchConfig })}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-700"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.stopOnMatch}
                onChange={(e) => setFormData({ ...formData, stopOnMatch: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-700"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Stop other rules on match
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-800 rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Rule'}
          </Button>
        </div>
    </>
  );

  if (inline) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col max-h-[calc(100vh-120px)] overflow-hidden">
        {inner}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        {inner}
      </div>
    </div>
  );
}
